import { useState } from "react";

import { api, type Me } from "../api";
import { useAction, useLoad } from "../useLoad";

interface Props {
  teamId: string;
  me: Me;
  revision: number;
  onError: (message: string) => void;
  onSignOut: () => void;
  /** Re-read `/me`, because renaming, creating and leaving all change the team list itself. */
  onTeamsChanged: () => void;
  onPickTeam: (teamId: string) => void;
}

/**
 * Task 8.3 — who is in the team and what they may do.
 *
 * Requirement 5.4 separates members who can advance races and manage the garage from members who can
 * only watch and upload media. Removing someone takes effect immediately rather than at token expiry:
 * the authorizer treats membership as the authority and re-checks it on every request, so a removed
 * member's next call fails even though their token is still technically valid.
 */
export function Roster({ teamId, me, revision, onError, onSignOut, onTeamsChanged, onPickTeam }: Props) {
  const { data, loading, reload } = useLoad(() => api.members(teamId), [teamId, revision], onError);
  const { busy, run } = useAction(onError);

  const members = data?.members ?? [];
  const myRole = members.find((member) => member.sub === me.sub)?.role ?? me.teams[0]?.role ?? "";
  const isOwner = myRole === "owner";
  const owners = members.filter((member) => member.role === "owner").length;

  return (
    <>
      <div className="card">
        <p className="section-title">
          {members.length === 1 ? "1 person" : `${members.length} people`}
          {loading ? " · refreshing" : ""}
        </p>
        <ul className="list">
          {members.map((member) => {
            const isMe = member.sub === me.sub;
            // The last owner cannot be demoted or removed, or the team becomes unmanageable. The API
            // enforces this too; showing it disabled is kinder than a rejection.
            const lastOwner = member.role === "owner" && owners === 1;
            return (
              <li key={member.sub} className="row">
                <span className="grow">
                  <span className="name">
                    {member.name || member.email || "Someone"}
                    {isMe && " (you)"}
                  </span>
                  {member.email && member.name && <div className="muted">{member.email}</div>}
                </span>
                <span className={`pill${member.role === "owner" ? " owner" : ""}`}>{member.role}</span>
                {isOwner && !lastOwner && (
                  <>
                    <button
                      className="btn small"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () =>
                            api.setRole(
                              teamId,
                              member.sub,
                              member.role === "owner" ? "member" : "owner",
                            ),
                          reload,
                        )
                      }
                    >
                      {member.role === "owner" ? "Make member" : "Make owner"}
                    </button>
                    {!isMe && (
                      <button
                        className="btn small danger"
                        disabled={busy}
                        onClick={() => void run(() => api.removeMember(teamId, member.sub), reload)}
                      >
                        Remove
                      </button>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
        {!isOwner && (
          <p className="muted" style={{ marginTop: 10 }}>
            Owners can change roles and remove people.
          </p>
        )}
      </div>

      <div className="card">
        <p className="section-title">Signed in</p>
        <div className="row">
          <span className="grow">
            <span className="name">{me.name || me.email || "You"}</span>
            {me.email && me.name && <div className="muted">{me.email}</div>}
          </span>
          <button className="btn small" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>

      <div className="card">
        <p className="section-title">Inviting people</p>
        <TeamAdmin teamId={teamId} isOwner={isOwner} onError={onError} onChanged={onTeamsChanged} />
      </div>

      <div className="card">
        <p className="section-title">Other teams</p>
        <TeamSwitcher me={me} teamId={teamId} onError={onError} onChanged={onTeamsChanged} onPick={onPickTeam} />
      </div>
    </>
  );
}

/**
 * Requirements 5.9 and 5.10 — rename the team, and hand somebody a link that gets them into it.
 *
 * The invite is a **link, not a code to read out.** Requirement 5.10 chose that shape because the whole
 * flow is then "paste this into a text message", with no directory to look anyone up in and no
 * pending-invite state to reconcile. So the primary control here is a copy button, and the code itself
 * is shown only small and only because a link that cannot be seen is hard to trust.
 *
 * The code is fetched lazily rather than with the roster. It is a capability (5.13), and there is no
 * reason for it to be sitting in the page for every member who opens the team tab to look at the
 * roster — an owner asks for it at the moment they intend to send it.
 */
function TeamAdmin({
  teamId,
  isOwner,
  onError,
  onChanged,
}: {
  teamId: string;
  isOwner: boolean;
  onError: (message: string) => void;
  onChanged: () => void;
}) {
  const { data: team } = useLoad(() => api.team(teamId), [teamId], onError);
  const [code, setCode] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [copied, setCopied] = useState(false);
  const { busy, run } = useAction(onError);

  const link = code ? `${window.location.origin}/join/${code}` : null;

  async function copy(text: string) {
    try {
      // `navigator.clipboard` needs a secure context, which rules it out on a phone testing over
      // plain HTTP on a LAN. The textarea fallback is not legacy cruft — it is the path that works
      // there, and this is a link whose entire purpose is to be copied.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const scratch = document.createElement("textarea");
        scratch.value = text;
        scratch.setAttribute("readonly", "");
        scratch.style.position = "fixed";
        scratch.style.opacity = "0";
        document.body.appendChild(scratch);
        scratch.select();
        document.execCommand("copy");
        document.body.removeChild(scratch);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Selecting the text by hand still works, so say that rather than nothing.
      onError("Couldn't copy automatically — select the link and copy it.");
    }
  }

  if (!isOwner) {
    return <p className="muted">Ask an owner for an invite link.</p>;
  }

  return (
    <>
      <div className="inline-form">
        <input
          type="text"
          placeholder={team?.name ?? "Team name"}
          value={name}
          maxLength={60}
          enterKeyHint="done"
          autoCapitalize="words"
          onChange={(event) => setName(event.target.value)}
          aria-label="Team name"
        />
        <button
          className="btn small"
          disabled={busy || !name.trim() || name.trim() === team?.name}
          onClick={() => void run(() => api.renameTeam(teamId, name), () => { setName(""); onChanged(); })}
        >
          Rename
        </button>
      </div>

      {link ? (
        <>
          <p className="muted" style={{ marginTop: 12 }}>
            Send this to whoever should join. Anyone who has it can join, so send it directly rather
            than posting it anywhere.
          </p>
          <div className="inline-form">
            <code className="invite-link">{link}</code>
            <button className="btn small" onClick={() => void copy(link)}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <button
            className="btn small"
            style={{ marginTop: 10 }}
            disabled={busy}
            onClick={() =>
              void run(
                () => api.invite(teamId).then((fresh) => setCode(fresh.joinCode)),
                onChanged,
              )
            }
          >
            Make a new link
          </button>
          <p className="muted">A new link stops the old one working.</p>
        </>
      ) : (
        <button
          className="btn"
          style={{ marginTop: 12 }}
          disabled={busy}
          onClick={() => void run(() => api.invite(teamId).then((fresh) => setCode(fresh.joinCode)))}
        >
          Get an invite link
        </button>
      )}
    </>
  );
}

/**
 * Requirement 5.8, and the end of `me.teams[0]`.
 *
 * The app hard-coded the first team for as long as there was only ever one way to have a team. Now a
 * person can be in their personal team and a family team at once (5.12 caps it there), so there has to
 * be a way to say which one you are looking at — and a way to make the family one in the first place.
 */
function TeamSwitcher({
  me,
  teamId,
  onError,
  onChanged,
  onPick,
}: {
  me: Me;
  teamId: string;
  onError: (message: string) => void;
  onChanged: () => void;
  onPick: (teamId: string) => void;
}) {
  const [name, setName] = useState("");
  const { busy, run } = useAction(onError);

  return (
    <>
      <ul className="list">
        {me.teams.map((team) => (
          <li key={team.teamId} className="row">
            <span className="grow">
              <span className="name">{team.name || "Untitled"}</span>
              <div className="muted">
                {team.personal ? "Just you" : team.role === "owner" ? "You own this" : "You're a member"}
              </div>
            </span>
            {team.teamId === teamId ? (
              <span className="pill">Showing</span>
            ) : (
              <button className="btn small" onClick={() => onPick(team.teamId)}>
                Show
              </button>
            )}
            {/*
              Requirement 5.12's departure, on its own rather than only as a side effect of joining
              somewhere else. Never offered for the personal team: requirement 5.3 guarantees a user
              always has one, so leaving it is not a state the product has.
            */}
            {!team.personal && (
              <button
                className="btn small danger"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm(`Leave ${team.name || "this team"}? You'd lose access to its races and history.`)) {
                    return;
                  }
                  void run(() => api.leaveTeam(team.teamId), () => {
                    onChanged();
                    if (team.teamId === teamId) {
                      const fallback = me.teams.find((other) => other.teamId !== team.teamId);
                      if (fallback) onPick(fallback.teamId);
                    }
                  });
                }}
              >
                Leave
              </button>
            )}
          </li>
        ))}
      </ul>

      <div className="inline-form" style={{ marginTop: 12 }}>
        <input
          type="text"
          placeholder="New team name"
          value={name}
          maxLength={60}
          enterKeyHint="done"
          autoCapitalize="words"
          onChange={(event) => setName(event.target.value)}
          aria-label="New team name"
        />
        <button
          className="btn small"
          disabled={busy || !name.trim()}
          onClick={() =>
            void run(
              () => api.createTeam(name).then((created) => onPick(created.teamId)),
              () => {
                setName("");
                onChanged();
              },
            )
          }
        >
          Create
        </button>
      </div>
    </>
  );
}
